import type { AlignmentPoints, SnapZoneInfo } from '@messenger/types'

type CalculateAlignmentSnapResult = {
  verticalSnapZone: SnapZoneInfo | null
  horizontalSnapZone: SnapZoneInfo | null
  snapInfo: {
    verticalSnap: number | null
    horizontalSnap: number | null
    verticalVisible: number[]
    horizontalVisible: number[]
  }
}

/** An item whose centre is inside this many page px of a guide snaps onto it. */
export const SNAP_ZONE_DISTANCE = 20

/**
 * Calculates snap zones for dragged items against alignment points.
 * Simple "snap on entry" behavior: when item enters zone, it snaps once to center.
 *
 * Creates a "snap zone" around each guide. When item first enters the zone,
 * it automatically snaps to the guide center, then allows free movement.
 *
 * @param itemCenterX - Current X position of item center
 * @param itemCenterY - Current Y position of item center
 * @param alignmentPoints - Array of x/y coordinates to snap to
 * @returns Snap zone info and guide visibility for rendering
 */
export const calculateAlignmentSnap = (
  itemCenterX: number,
  itemCenterY: number,
  alignmentPoints: AlignmentPoints,
): CalculateAlignmentSnapResult => {
  const { vertical, horizontal } = alignmentPoints

  // Snap zone configuration:
  // - Snap zone: 0-20px from guide (snap on entry)
  // - Visible zone: guideline appears when approaching snap area
  // Note: When item enters snap zone, it snaps to center once, then allows free movement
  //       Snap zone size (20px) provides good UX - easy to hit, not overly aggressive
  const VISIBLE_DISTANCE = 90

  let verticalSnapZone: SnapZoneInfo | null = null
  let horizontalSnapZone: SnapZoneInfo | null = null
  let closestVerticalDistance = Infinity
  let closestHorizontalDistance = Infinity

  const verticalVisible: number[] = []
  const horizontalVisible: number[] = []
  let verticalSnapGuide: number | null = null
  let horizontalSnapGuide: number | null = null

  // Process vertical guides (horizontal position snapping)
  vertical?.forEach((guidePosition) => {
    // Skip invalid guide positions
    if (typeof guidePosition !== 'number' || !isFinite(guidePosition)) {
      return
    }

    // Calculate distance from guide
    const distance = Math.abs(itemCenterX - guidePosition)

    // Show guide if within visible range
    if (distance <= VISIBLE_DISTANCE) {
      verticalVisible.push(guidePosition)
    }

    // Check if within snap zone (for snap-on-entry behavior)
    if (distance <= SNAP_ZONE_DISTANCE && distance < closestVerticalDistance) {
      closestVerticalDistance = distance

      // For snap-on-entry, we just need to track the guide position
      // No progressive strength needed - we snap once on entry, then free movement
      verticalSnapZone = {
        guidePosition,
        snapStrength: 1.0, // Not used for snap-on-entry, but kept for compatibility
      }
      verticalSnapGuide = guidePosition
    }
  })

  // Process horizontal guides (vertical position snapping)
  horizontal?.forEach((guidePosition) => {
    // Skip invalid guide positions
    if (typeof guidePosition !== 'number' || !isFinite(guidePosition)) {
      return
    }

    const distance = Math.abs(itemCenterY - guidePosition)

    if (distance <= VISIBLE_DISTANCE) {
      horizontalVisible.push(guidePosition)
    }

    if (
      distance <= SNAP_ZONE_DISTANCE &&
      distance < closestHorizontalDistance
    ) {
      closestHorizontalDistance = distance

      // For snap-on-entry, we just need to track the guide position
      horizontalSnapZone = {
        guidePosition,
        snapStrength: 1.0, // Not used for snap-on-entry, but kept for compatibility
      }
      horizontalSnapGuide = guidePosition
    }
  })

  return {
    verticalSnapZone,
    horizontalSnapZone,
    snapInfo: {
      verticalSnap: verticalSnapGuide,
      horizontalSnap: horizontalSnapGuide,
      verticalVisible,
      horizontalVisible,
    },
  }
}
